import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ColumnResizeHandleProps } from './ColumnResizeHandle';

export interface ColumnResizeOptions {
  /** Width for a column that has no entry in `defaults`. */
  fallbackWidth?: number;
  /** Narrowest a column may be dragged — one value for every column, or per column key. */
  minWidth?: number | Record<string, number>;
  /** Widest a column may be dragged — one value for every column, or per column key. */
  maxWidth?: number | Record<string, number>;
  /**
   * Leave the table on its own (auto, fit-to-screen) layout until the first
   * resize, then capture every column's on-screen width as the starting point.
   * Headers must carry `data-column-key`; until `isFixed` turns true the
   * caller renders its original layout and ignores `getWidth`.
   */
  measureOnFirstResize?: boolean;
}

const DEFAULT_FALLBACK_WIDTH = 160;
const DEFAULT_MIN_WIDTH = 60;
const DEFAULT_MAX_WIDTH = 800;

/** Attribute that marks a header cell for `measureOnFirstResize`. */
const COLUMN_KEY_ATTR = 'data-column-key';

/** Set on <html> while a drag is in progress — see `index.css`. */
const RESIZING_CLASS = 'is-col-resizing';

/**
 * Drag-to-resize state for a `table-layout: fixed` table.
 *
 * Only widths the user has changed are held in state; every other column
 * reads its baseline — the widths measured at the first resize, or else its
 * default — so a reset is just dropping the key. Widths live for the lifetime
 * of the component — nothing is persisted.
 *
 * Pair with `<ColumnResizeHandle {...handleProps(key)} />` in each header and
 * `<col style={{ width: getWidth(key) }} />` in the table's `<colgroup>`.
 */
export function useColumnResize(defaults: Record<string, number> = {}, options: ColumnResizeOptions = {}) {
  const {
    fallbackWidth = DEFAULT_FALLBACK_WIDTH,
    minWidth = DEFAULT_MIN_WIDTH,
    maxWidth = DEFAULT_MAX_WIDTH,
    measureOnFirstResize = false,
  } = options;

  const [widths, setWidths] = useState<Record<string, number>>({});
  /** On-screen widths captured at the first resize (`measureOnFirstResize` only). */
  const [measured, setMeasured] = useState<Record<string, number> | null>(null);
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const endDragRef = useRef<(() => void) | null>(null);

  // A drag still in flight when the table unmounts must not leave listeners
  // or the global cursor behind.
  useEffect(() => () => endDragRef.current?.(), []);

  const isFixed = !measureOnFirstResize || measured !== null;

  const widthFrom = (key: string, base: Record<string, number> | null) =>
    widths[key] ?? base?.[key] ?? defaults[key] ?? fallbackWidth;
  const getWidth = (key: string) => widthFrom(key, measured);
  const getMinWidth = (key: string) => (typeof minWidth === 'number' ? minWidth : minWidth[key] ?? DEFAULT_MIN_WIDTH);
  const getMaxWidth = (key: string) => (typeof maxWidth === 'number' ? maxWidth : maxWidth[key] ?? DEFAULT_MAX_WIDTH);

  /**
   * Bounds for a resize that starts at `from`. A column the auto layout already
   * made wider than the max (or narrower than the min) may move back toward
   * the range but not further out — otherwise grabbing it would snap it.
   */
  const clampFrom = (key: string, from: number, width: number) => {
    const lo = Math.min(getMinWidth(key), from);
    const hi = Math.max(getMaxWidth(key), from);
    return Math.min(hi, Math.max(lo, Math.round(width)));
  };

  const setWidth = (key: string, width: number) =>
    setWidths((prev) => (prev[key] === width ? prev : { ...prev, [key]: width }));

  /**
   * Widths to resize from. On the first resize of a `measureOnFirstResize`
   * table, reads every marked header's current width off the page and keeps it
   * as the baseline, so switching to the fixed layout moves nothing.
   */
  const ensureBaseline = (fromEl: HTMLElement): Record<string, number> | null => {
    if (!measureOnFirstResize || measured) return measured;
    const table = fromEl.closest('table');
    if (!table) return null;
    const next: Record<string, number> = {};
    table.querySelectorAll<HTMLElement>(`[${COLUMN_KEY_ATTR}]`).forEach((th) => {
      const key = th.getAttribute(COLUMN_KEY_ATTR);
      if (key) next[key] = th.getBoundingClientRect().width;
    });
    setMeasured(next);
    return next;
  };

  const startResize = (key: string, e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    endDragRef.current?.();

    const startX = e.clientX;
    const startWidth = widthFrom(key, ensureBaseline(e.currentTarget));

    const onMove = (ev: PointerEvent) => setWidth(key, clampFrom(key, startWidth, startWidth + ev.clientX - startX));

    const endDrag = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', endDrag);
      document.documentElement.classList.remove(RESIZING_CLASS);
      endDragRef.current = null;
      setResizingKey(null);
    };

    const onUp = () => {
      endDrag();
      // A drag that ends over its header is followed by a click on that
      // header, which on a sortable column would re-sort the table. Swallow
      // exactly that one click; the timeout drops the guard if none comes.
      const swallow = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', endDrag);
    document.documentElement.classList.add(RESIZING_CLASS);
    endDragRef.current = endDrag;
    setResizingKey(key);
  };

  const nudge = (key: string, delta: number, fromEl: HTMLElement) => {
    const from = widthFrom(key, ensureBaseline(fromEl));
    setWidth(key, clampFrom(key, from, from + delta));
  };

  /** Back to the baseline: the measured width, or the default. */
  const resetWidth = (key: string) => {
    setWidths((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Stable, so callers can reset from an effect keyed on what the table shows.
  // Also drops the measured baseline, returning the table to its own layout.
  const resetAll = useCallback(() => {
    setWidths({});
    setMeasured(null);
  }, []);

  const handleProps = (key: string): Omit<ColumnResizeHandleProps, 'label'> => ({
    // Unknown until measured — the auto layout decides it, not this hook.
    width: isFixed ? getWidth(key) : undefined,
    minWidth: getMinWidth(key),
    maxWidth: getMaxWidth(key),
    active: resizingKey === key,
    onResizeStart: (e) => startResize(key, e),
    onReset: () => resetWidth(key),
    onNudge: (delta, el) => nudge(key, delta, el),
  });

  return { widths, getWidth, isFixed, handleProps, resetWidth, resetAll };
}
